import * as React from "react"
import { Check, ChevronDown, Search } from "lucide-react"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { useTranslation } from "react-i18next"
import { cn } from "../lib/utils"
import HighlightText from "./highlight-text"
import { PortalContainerContext } from './portal-container.jsx';
import { controlBase, controlDisabled, controlFocus, menuItem, menuSurface } from '../styles/recipes.js';

const SelectContext = React.createContext({})

// Utility to recursively extract text from children
const getTextFromChildren = (children) => {
  let text = "";
  React.Children.forEach(children, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      text += child;
    } else if (child && child.props && child.props.children !== undefined) {
      text += getTextFromChildren(child.props.children);
    }
  });
  return text;
};

// Utility to recursively Find SelectItems and build map
const getMapFromChildren = (children, map = {}) => {
  React.Children.forEach(children, (child) => {
    if (!child) return;

    // If it's a SelectItem (check logic can be improved via displayName or similar)
    // Note: Checking child.type === SelectItem usually works if imported, but in React context across files it's safer to check props.
    // However, for this to work robustly, we assume standard usage.
    if (child.props && (child.props.value !== undefined) && child.props.children) {
      // We found a candidate.
      // If it's inside a SelectContent, we should grab it.
      // We don't check for specific component type strictly to be flexible, 
      // but we assume anything with 'value' prop inside SelectContent structure is relevant if it matches structure.
      // Better: We rely on finding SelectItem by traversing. 
    }

    if (child.props && child.props.value !== undefined && child.props.value !== null && !Object.prototype.hasOwnProperty.call(map, child.props.value)) {
      map[child.props.value] = child.props.displayValue ?? child.props.children;
    }

    if (child.props && child.props.children !== undefined) {
      getMapFromChildren(child.props.children, map);
    }
  });
  return map;
}


function Select({
  children,
  value: controlledValue,
  defaultValue,
  onValueChange,
  open: controlledOpen,
  defaultOpen,
  onOpenChange,
  disabled,
  dir,
  name,
  autoComplete,
  ...props
}) {
  const [value, setValue] = React.useState(defaultValue ?? "")
  const effectiveValue = controlledValue !== undefined ? controlledValue : value

  const [open, setOpen] = React.useState(defaultOpen ?? false)
  const effectiveOpen = controlledOpen !== undefined ? controlledOpen : open

  // Search Term
  const [searchTerm, setSearchTerm] = React.useState("");

  const handleValueChange = React.useCallback(
    (newValue) => {
      // If uncontrolled, update internal state
      if (controlledValue === undefined) {
        setValue(newValue)
      }
      onValueChange?.(newValue)
      if (controlledOpen === undefined) {
        setOpen(false)
      } else {
        onOpenChange?.(false)
      }
      setSearchTerm(""); // Reset search on select
    },
    [controlledValue, onValueChange, controlledOpen, onOpenChange]
  )

  const handleOpenChange = React.useCallback((newOpen) => {
    if (controlledOpen === undefined) {
      setOpen(newOpen)
    }
    onOpenChange?.(newOpen)
    if (newOpen) {
      setSearchTerm(""); // Reset search on open
    }
  }, [controlledOpen, onOpenChange]);

  // Recursively map descendant values to labels for display
  const labelMap = React.useMemo(() => {
    return getMapFromChildren(children);
  }, [children]);

  return (
    <SelectContext.Provider value={{
      value: effectiveValue,
      onValueChange: handleValueChange,
      open: effectiveOpen,
      onOpenChange: handleOpenChange,
      labelMap,
      searchTerm,
      setSearchTerm,
      disabled,
      dir,
      name,
      autoComplete,
    }}>
      <PopoverPrimitive.Root {...props} data-slot="select" open={effectiveOpen} onOpenChange={handleOpenChange}>
        {children}
      </PopoverPrimitive.Root>
    </SelectContext.Provider>
  )
}

const SelectTrigger = React.forwardRef(({ className, children, disabled: triggerDisabled, ...props }, ref) => {
  const { disabled, open } = React.useContext(SelectContext);
  const isDisabled = disabled || triggerDisabled;
  return (
    <PopoverPrimitive.Trigger
      ref={ref}
      {...props}
      disabled={isDisabled}
      data-slot="select-trigger"
      aria-haspopup="listbox"
      aria-expanded={open}
      className={cn(
        controlBase,
        "flex h-[var(--ds-density-control-height)] w-full items-center justify-between px-[var(--ds-control-padding-inline)] py-[var(--ds-space-2)] text-sm placeholder:text-muted-foreground",
        controlFocus,
        controlDisabled,
        "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/35",
        className
      )}
    >
      {children}
      <ChevronDown className="size-[var(--ds-icon-size-sm)] shrink-0 opacity-50" aria-hidden />
    </PopoverPrimitive.Trigger>
  )
})
SelectTrigger.displayName = "SelectTrigger"

const SelectValue = React.forwardRef(({ className, placeholder, ...props }, ref) => {
  const { value, labelMap } = React.useContext(SelectContext)

  // Normalize value: objects cannot be rendered as React children, treat them as "no selection"
  const primitiveValue = (value !== null && value !== undefined && typeof value !== 'object')
    ? value
    : null;
  const hasLabel = primitiveValue !== null && primitiveValue !== undefined && Object.prototype.hasOwnProperty.call(labelMap, primitiveValue);
  const displayValue = hasLabel ? labelMap[primitiveValue] : primitiveValue;
  const hasDisplayValue = displayValue !== null && displayValue !== undefined && displayValue !== '';

  return (
    <span ref={ref} className={cn("block truncate", className)} {...props}>
      {hasDisplayValue
        ? displayValue
        : <span className="text-muted-foreground">{placeholder}</span>}
    </span>
  )
})
SelectValue.displayName = "SelectValue"

const SelectContent = React.forwardRef(({
  className,
  children,
  position = "popper",
  onWheel,
  onKeyDown,
  onOpenAutoFocus,
  searchable = true,
  searchPlaceholder,
  container,
  style,
  ...props
}, ref) => {
  const { searchTerm, setSearchTerm, onOpenChange } = React.useContext(SelectContext);
  const { t } = useTranslation("common");
  const resolvedSearchPlaceholder = searchPlaceholder ?? t("actions.search", { defaultValue: "Search" });
  const portalContainer = React.useContext(PortalContainerContext);

  const getEnabledOptions = (content) => [...(content?.querySelectorAll('[role="option"]') ?? [])]
    .filter((item) => item.getAttribute('aria-disabled') !== 'true');

  const focusOption = (content, index) => {
    const options = getEnabledOptions(content);
    options[index]?.focus();
  };

  const handleOpenAutoFocus = (event) => {
    onOpenAutoFocus?.(event);
    if (event.defaultPrevented || searchable) return;

    event.preventDefault();
    const content = event.currentTarget;
    const options = getEnabledOptions(content);
    const selected = options.find((option) => option.getAttribute('aria-selected') === 'true');
    (selected ?? options[0])?.focus();
  };

  const handleKeyDown = (event) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;

    const optionTarget = event.target.closest?.('[role="option"]');
    if (optionTarget) return;

    const options = getEnabledOptions(event.currentTarget);
    if (!options.length) return;

    const activeElement = event.currentTarget.ownerDocument?.activeElement;
    const activeIndex = options.indexOf(activeElement);
    const selectedIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    const fallbackIndex = selectedIndex >= 0 ? selectedIndex : 0;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusOption(event.currentTarget, activeIndex >= 0 ? (activeIndex + 1) % options.length : fallbackIndex);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusOption(event.currentTarget, activeIndex >= 0 ? (activeIndex - 1 + options.length) % options.length : options.length - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusOption(event.currentTarget, 0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusOption(event.currentTarget, options.length - 1);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onOpenChange?.(false);
    }
  };

  return (
    <PopoverPrimitive.Portal container={container === undefined ? portalContainer : container}>
      <PopoverPrimitive.Content
        ref={ref}
        data-slot="select-content"
        className={cn(
          `relative z-[var(--ds-layer-transient)] flex flex-col min-w-[var(--ds-menu-min-width)] overflow-hidden ${menuSurface} data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-1.5 data-[side=left]:slide-in-from-right-1.5 data-[side=right]:slide-in-from-left-1.5 data-[side=top]:slide-in-from-bottom-1.5`,
          position === "popper" && "data-[side=bottom]:translate-y-[var(--ds-space-1)] data-[side=left]:-translate-x-[var(--ds-space-1)] data-[side=right]:translate-x-[var(--ds-space-1)] data-[side=top]:-translate-y-[var(--ds-space-1)]",
          "max-h-[var(--ds-menu-max-height)]",
          className
        )}
        // Match standard Select Content width
        style={{
          width: 'var(--radix-popover-trigger-width)',
          maxHeight: 'var(--radix-popover-content-available-height, var(--ds-menu-max-height))',
          ...style,
        }}
        onWheel={(e) => {
          onWheel?.(e);
          e.stopPropagation();
        }}
        onOpenAutoFocus={handleOpenAutoFocus}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {searchable ? (
          <div className="flex shrink-0 items-center border-b border-border-subtle px-[var(--ds-space-3)]" cmdk-input-wrapper="">
            <Search className="mr-[var(--ds-space-2)] size-[var(--ds-icon-size-sm)] shrink-0 text-muted-foreground" aria-hidden />
            <input
              data-slot="select-search"
              role="searchbox"
              aria-label={resolvedSearchPlaceholder}
              className={cn(
                "flex h-[var(--ds-density-control-height)] w-full rounded-[var(--ds-radius-control)] bg-transparent py-[var(--ds-space-3)] text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              )}
              placeholder={resolvedSearchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
        ) : null}
        <div
          data-slot="select-list"
          role="listbox"
          className="min-h-0 flex-1 overflow-y-auto p-[var(--ds-space-1)] [scrollbar-color:var(--ds-scrollbar-thumb)_transparent]"
          onWheel={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
})
SelectContent.displayName = "SelectContent"

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="select-label"
    className={cn("px-[var(--ds-space-2)] py-[var(--ds-space-1-5)] text-xs font-bold uppercase tracking-[0.08em] text-muted-foreground", className)}
    {...props}
  />
))
SelectLabel.displayName = "SelectLabel"

const SelectItem = React.forwardRef(({
  className,
  children,
  value,
  textValue,
  displayValue,
  renderContent = false,
  disabled = false,
  onClick,
  onKeyDown,
  ...props
}, ref) => {
  const {
    value: selectedValue,
    onValueChange,
    onOpenChange,
    searchTerm,
    disabled: selectDisabled,
  } = React.useContext(SelectContext)
  const isDisabled = disabled || selectDisabled
  const isSelected = selectedValue === value

  // Filter Logic:
  // If search term exists, check if children text content matches
  const textContent = textValue ?? getTextFromChildren(children)
  if (searchTerm && !textContent.toLowerCase().includes(searchTerm.toLowerCase())) {
    return null;
  }

  const moveFocus = (event, direction) => {
    const list = event.currentTarget.closest('[role="listbox"]')
    const items = [...(list?.querySelectorAll('[role="option"]') ?? [])]
      .filter((item) => item.getAttribute('aria-disabled') !== 'true')
    const index = items.indexOf(event.currentTarget)
    const nextIndex = direction === 'first'
      ? 0
      : direction === 'last'
        ? items.length - 1
        : (index + direction + items.length) % items.length
    items[nextIndex]?.focus()
  }

  const handleKeyDown = (event) => {
    if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      onValueChange(value)
    } else if (!isDisabled && event.key === 'ArrowDown') {
      event.preventDefault()
      moveFocus(event, 1)
    } else if (!isDisabled && event.key === 'ArrowUp') {
      event.preventDefault()
      moveFocus(event, -1)
    } else if (!isDisabled && event.key === 'Home') {
      event.preventDefault()
      moveFocus(event, 'first')
    } else if (!isDisabled && event.key === 'End') {
      event.preventDefault()
      moveFocus(event, 'last')
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onOpenChange?.(false)
    }
    onKeyDown?.(event)
  }

  return (
    <div
      ref={ref}
      role="option"
      tabIndex={isDisabled ? -1 : 0}
      aria-selected={isSelected}
      aria-disabled={isDisabled || undefined}
      data-slot="select-item"
      data-disabled={isDisabled || undefined}
      className={cn(
        menuItem,
        "w-full px-[var(--ds-space-2-5)] py-[var(--ds-space-2)]",
        isSelected && "bg-accent text-accent-foreground",
        "hover:bg-accent hover:text-accent-foreground",
        className
      )}
      onClick={(event) => {
        if (isDisabled) return
        onClick?.(event)
        if (!event.defaultPrevented) onValueChange(value)
      }}
      onKeyDown={handleKeyDown}
      aria-label={props['aria-label'] || (typeof displayValue === 'string' ? displayValue : undefined)}
      {...props}
    >
      <span className="absolute right-[var(--ds-space-2)] flex size-[var(--ds-icon-size-xs)] items-center justify-center">
        {isSelected && (
          <Check className="size-[var(--ds-icon-size-sm)]" />
        )}
      </span>
      {renderContent ? children : (
        <span className="truncate">
          <HighlightText text={textContent} highlight={searchTerm} />
        </span>
      )}
    </div>
  )
})
SelectItem.displayName = "SelectItem"

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="select-separator"
    role="separator"
    className={cn("-mx-[var(--ds-space-1)] my-[var(--ds-space-1)] h-px bg-muted", className)}
    {...props}
  />
))
SelectSeparator.displayName = "SelectSeparator"

// Unused dummy components to maintain API compatibility if imported
const SelectGroup = React.forwardRef(({ className, children, ...props }, ref) => {
  const { searchTerm } = React.useContext(SelectContext);

  if (searchTerm) {
    const hasMatch = React.Children.toArray(children).some((child) => {
      // We only check SelectItem visibility. SelectLabel should not trigger a group showing up if searched directly (usually).
      if (child.type?.displayName === "SelectItem") {
        const text = getTextFromChildren(child.props.children);
        return text.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return false;
    });

    if (!hasMatch) return null;
  }

  return <div ref={ref} data-slot="select-group" role="group" className={className} {...props}>{children}</div>
})
SelectGroup.displayName = "SelectGroup"

const SelectScrollDownButton = () => null
const SelectScrollUpButton = () => null

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
